import React, { useState, useEffect } from 'react';
import { TableContext } from '../context/TableContext';
import { AuthContext } from '../context/AuthContext';
import { 
  PlusIcon,
  TrashIcon
} from '@heroicons/react/outline';

const TableManagementPage = () => {
  const { tables, createTable, deleteTable, fetchTables } = React.useContext(TableContext);
  const { user } = React.useContext(AuthContext);
  const [newTable, setNewTable] = useState({
    name: '',
    description: '',
    type: 'grade',
    columns: [
      { name: 'Student', type: 'text' },
      { name: 'Score', type: 'number' }
    ]
  });

  // Fetch tables when component mounts
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'teacher')) {
      fetchTables();
    }
  }, [user, fetchTables]);

  const handleCreateTable = async () => {
    if (user.role === 'admin') {
      try {
        await createTable(newTable);
        setNewTable({
          name: '',
          description: '',
          type: 'grade',
          columns: [
            { name: 'Student', type: 'text' },
            { name: 'Score', type: 'number' }
          ]
        });
      } catch (error) {
        console.error('Error creating table:', error);
      }
    }
  };

  const handleDelete = async (tableId) => {
    if (user.role === 'admin') {
      if (window.confirm('Are you sure you want to delete this table?')) {
        try {
          await deleteTable(tableId);
        } catch (error) {
          console.error('Error deleting table:', error);
        }
      }
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Table Management</h2>
      
      {user.role === 'admin' && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Create New Table</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="tableName" className="block text-sm font-medium text-gray-700">Table Name</label>
              <input
                id="tableName"
                type="text"
                value={newTable.name}
                onChange={e => setNewTable(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="tableDescription" className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                id="tableDescription"
                value={newTable.description}
                onChange={e => setNewTable(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleCreateTable}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Create Table
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Level</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tables.map(table => (
              <tr key={table.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{table.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {table.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{table.description}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                        style={{
                          backgroundColor: table.accessLevel === 'public' ? '#E6F4EA' : '#FDF2F2',
                          color: table.accessLevel === 'public' ? '#16A34A' : '#DC2626'
                        }}
                  >
                    {table.accessLevel === 'public' ? 'Public' : 'Private'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {user.role === 'admin' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDelete(table.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableManagementPage;
